import { DateFilterConfig } from '../types/filter-config';
import { formatDate, IsoDate, PathOfType, QueryFilter, Range, uuid } from '@softline/core';
import { FilterItemConfigOptions } from '../types/filter-item-config-options';
import { DateFilterOperator } from '../types/filter-operators';
import { FilterStateFactoryRef } from './filter.function';
import { DateBetweenFilter, DateFilter } from '../types/filter';
import { defaultFilterInputTypeFactory } from '../page-filter/helper/default-filter-input-type.factory';

export interface DateFilterOptions<T extends object> extends FilterItemConfigOptions<T, IsoDate> {
  defaultOperator?: DateFilterOperator;
  allowedOperators?: DateFilterOperator[];
}

export function dateFilter<T extends object>(
  ref: FilterStateFactoryRef<T>,
  options: DateFilterOptions<T>): void {
  ref.addItem({
    ...options,
    type: 'date',
    defaultOperator: options?.defaultOperator ?? 'equals',
    allowedOperators: options.allowedOperators ?? ['equals', 'notEquals', 'before', 'after', 'beforeOrEquals', 'afterOrEquals', 'in', 'notIn', 'between', 'empty', 'notEmpty'],
    formatter: options.formatter ?? ((o) => formatDate(o)),
    toQueryFilter,
    inputTypeFactory: defaultFilterInputTypeFactory,
  } as DateFilterConfig<T>);
}

function toQueryFilter<T extends object>(value: DateFilter<T>): QueryFilter<T> {
  switch (value.operator) {
    case 'empty':
      return { name: value.field, type: 'dateEmpty' }
    case 'notEmpty':
      return { name: value.field, type: 'dateNotEmpty' }
    case 'equals':
      return { name: value.field, type: 'dateEquals', value: value.value }
    case 'notEquals':
      return { name: value.field, type: 'dateNotEquals', value: value.value }
    case 'before':
      return { name: value.field, type: 'dateBefore', value: value.value }
    case 'after':
      return { name: value.field, type: 'dateAfter', value: value.value }
    case 'beforeOrEquals':
      return { name: value.field, type: 'dateBeforeOrEquals', value: value.value }
    case 'afterOrEquals':
      return { name: value.field, type: 'dateAfterOrEquals', value: value.value }
    case 'between': {
      const primary = { name: value.field, type: 'dateInRange', valueFrom: value.value.from, valueTo: value.value.to } as QueryFilter<T>;
      if (!value.reference) return primary;
      const reference = { name: value.field, type: 'dateInRange', valueFrom: value.reference.from, valueTo: value.reference.to } as QueryFilter<T>;
      return { type: 'conditions', operator: 'OR', filters: [primary, reference] } as QueryFilter<T>;
    }
    case 'in':
      throw new Error('operator in not supported yet by the api');
    case 'notIn':
      throw new Error('operator notIn not supported yet by the api');
  }
}

/** A ready-made between node for filter({ initialValue }) and the date items, so consumers never hand-write uuid/type/operator. */
export function createDateRangeFilter<T extends object>(
  field: PathOfType<T, IsoDate>,
  value: Range<IsoDate>,
  reference?: Range<IsoDate> | null
): DateBetweenFilter<T> {
  const node: DateBetweenFilter<T> = { uuid: uuid(), type: 'date', field, operator: 'between', value };
  if (reference) node.reference = reference;
  return node;
}
